import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Gift, ArrowRight, Users, CheckCircle2, Timer, Award } from "lucide-react";

function initials(name) {
  const parts = (name || "").split(" ").filter(Boolean);
  return ((parts[0]?.[0] || "F") + (parts[1]?.[0] || "")).toUpperCase();
}

export default function Founding100() {
  const [wall, setWall] = useState(null);
  useEffect(() => { api.get("/founders/wall").then((r) => setWall(r.data)).catch(()=>{}); }, []);

  const claimed = wall?.claimed || 0;
  const quota = wall?.quota || 100;
  const remaining = wall?.remaining ?? (quota - claimed);
  const pct = Math.min(100, (claimed / quota) * 100);

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-widest mb-6" data-testid="founding-badge">
            <Sparkles className="w-3.5 h-3.5" /> The Founding 100
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] max-w-4xl mx-auto">
            Be one of our first 100 users.<br />
            <span className="brand-gradient-text">Your first case is free.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            No card. No trial. No catch. Just help us prove this works — and get $19 worth of refund concierge on the house.
          </p>

          <div className="mt-8 flex justify-center flex-col sm:flex-row gap-3 items-center">
            <Link to="/auth" data-testid="founding-cta-primary">
              <Button size="lg" className="w-full sm:w-auto rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-14 text-base font-semibold">
                <Gift className="w-4 h-4 mr-2" /> Claim my free case
              </Button>
            </Link>
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-900" data-testid="founding-back-link">
              or start a regular $19 case →
            </Link>
          </div>

          {/* Progress meter */}
          <div className="mt-12 max-w-xl mx-auto text-left">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold text-slate-900" data-testid="founding-progress-count">{claimed} of {quota} claimed</span>
              <span className="text-slate-500" data-testid="founding-remaining">{remaining} spots remaining</span>
            </div>
            <Progress value={pct} className="h-3" />
            {remaining > 0 && remaining <= 25 && (
              <p className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid="founding-scarcity">
                <Timer className="w-3.5 h-3.5" /> Only {remaining} left — going fast.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* What founders get */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight text-center">What you get as a founder</h2>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            <Card className="trust-card">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-3">
                  <Gift className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900">Your first case free</h3>
                <p className="text-sm text-slate-600 mt-2">Skip the $19 paywall on your very first refund case. All 4 messages unlocked instantly.</p>
              </CardContent>
            </Card>
            <Card className="trust-card">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-3">
                  <Award className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900">A shareable founder badge</h3>
                <p className="text-sm text-slate-600 mt-2">A gradient "Founder #N" card you can post to TikTok / IG / X. Small bragging rights, big word-of-mouth.</p>
              </CardContent>
            </Card>
            <Card className="trust-card">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-3">
                  <Users className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900">A permanent wall spot</h3>
                <p className="text-sm text-slate-600 mt-2">Your name (first + last initial) lives on the founder wall on our homepage. Forever.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live wall */}
      {wall?.founders?.length > 0 && (
        <section className="bg-[#FAFBFD] border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight text-center">Join these founders</h2>
            <div className="mt-8 flex flex-wrap gap-3 justify-center" data-testid="founding-wall">
              {wall.founders.slice(0, 40).map((f) => (
                <div key={f.founding_number} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-1 pr-3 py-1">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-800 text-white text-xs font-bold grid place-items-center">
                    {initials(f.display_name)}
                  </div>
                  <span className="text-xs font-medium text-slate-700">{f.display_name}</span>
                  <span className="text-[10px] text-slate-400">#{f.founding_number}</span>
                </div>
              ))}
              {wall.claimed > 40 && (
                <div className="flex items-center px-3 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-500">
                  + {wall.claimed - 40} more
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 text-emerald-700 mb-3">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-widest">No card required</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Ready to grab your spot?</h2>
          <p className="text-slate-600 mt-3">Sign up in 20 seconds. Your free case unlocks the moment you create a case.</p>
          <Link to="/auth" data-testid="founding-cta-bottom">
            <Button size="lg" className="mt-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-14 text-base font-semibold">
              Claim my free case <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-slate-500 mt-4 max-w-md mx-auto">
            Free case applies to your first case only. Additional cases are $19 each. Non-refundable. Refunds are not guaranteed.
          </p>
        </div>
      </section>
    </div>
  );
}
